import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoginPage } from '@/pages/LoginPage';
import { SkeletonGrid } from '@/components/feedback/Skeleton';

/*
  LAS PANTALLAS SE CARGAN AL ENTRAR EN ELLAS, NO ANTES. UC-046 / AC-55.

  Con todo importado de golpe el navegador se bajaba la aplicacion entera
  —1,12 MB sin comprimir, 344 KB por el cable— solo para pintar el formulario de
  acceso. Ahi viajaban el calendario de la agenda y las graficas del dashboard,
  que en el login no se usan y que son lo mas pesado del paquete. Medido sobre
  produccion: 2,29 s de LCP en Slow 4G, por encima de los 2 s que pide el AC.

  El login SI se importa de forma normal: es lo primero que ve cualquiera, y
  hacerle esperar a un segundo fichero para pintar la primera pantalla seria
  cambiar un problema por otro.

  React.lazy espera un export por defecto y aqui las paginas se exportan por
  nombre; de ahi el `.then` de cada linea. Se escribe una por una a proposito, en
  vez de con un ayudante generico, porque un ayudante borra los tipos de las
  props —`PaymentResultPage` recibe `resultado`— y el error no aparece hasta que
  algo se rompe en tiempo de ejecucion.
*/
const ForgotPasswordPage = lazy(() =>
  import('@/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const PublicBookingPage = lazy(() =>
  import('@/pages/PublicBookingPage').then((m) => ({ default: m.PublicBookingPage })),
);
const PaymentResultPage = lazy(() =>
  import('@/pages/PaymentResultPage').then((m) => ({ default: m.PaymentResultPage })),
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const LeadsPage = lazy(() => import('@/pages/LeadsPage').then((m) => ({ default: m.LeadsPage })));
const ClientsPage = lazy(() =>
  import('@/pages/ClientsPage').then((m) => ({ default: m.ClientsPage })),
);
const ClientDetailPage = lazy(() =>
  import('@/pages/ClientDetailPage').then((m) => ({ default: m.ClientDetailPage })),
);
const PaymentsPage = lazy(() =>
  import('@/pages/PaymentsPage').then((m) => ({ default: m.PaymentsPage })),
);
const MonitorPage = lazy(() =>
  import('@/pages/MonitorPage').then((m) => ({ default: m.MonitorPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const AgendaPage = lazy(() =>
  import('@/pages/AgendaPage').then((m) => ({ default: m.AgendaPage })),
);
const AgendaSettingsPage = lazy(() =>
  import('@/pages/AgendaSettingsPage').then((m) => ({ default: m.AgendaSettingsPage })),
);
const PlansPage = lazy(() => import('@/pages/PlansPage').then((m) => ({ default: m.PlansPage })));

/**
 * Espera de las rutas publicas, que no tienen la estructura del panel detras.
 * Las rutas de dentro del panel usan la suya, colocada alrededor del `Outlet`
 * en AppLayout, para que el menu lateral no parpadee en cada navegacion.
 */
function CargandoPantalla() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gn-bg p-6">
      <div className="w-full max-w-md">
        <SkeletonGrid count={3} variant="row" />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<CargandoPantalla />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* UC-061 / AC-73 — Ruta pública sin ProtectedRoute */}
        <Route path="/cita" element={<PublicBookingPage />} />
        {/*
          UC-019 — Vuelta desde Stripe Checkout. PÚBLICAS a propósito: las abre un
          cliente que acaba de pagar y no tiene cuenta en el panel. Sin estas dos
          rutas, la SPA no reconoce la ruta y le manda al login, que es lo que pasó
          en la primera prueba real de pago.
        */}
        <Route path="/pago-ok" element={<PaymentResultPage resultado="ok" />} />
        <Route path="/pago-cancelado" element={<PaymentResultPage resultado="cancelado" />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/:id" element={<ClientDetailPage />} />
          <Route path="agenda" element={<AgendaPage />} />
          <Route
            path="agenda/settings"
            element={
              <ProtectedRoute requireRole="admin">
                <AgendaSettingsPage />
              </ProtectedRoute>
            }
          />
          {/* UC-083 — Solo admin: gestionar planes es tocar lo que se cobra. */}
          <Route
            path="plans"
            element={
              <ProtectedRoute requireRole="admin">
                <PlansPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="payments"
            element={
              <ProtectedRoute requireRole="admin">
                <PaymentsPage />
              </ProtectedRoute>
            }
          />
          <Route path="monitor" element={<MonitorPage />} />
          <Route
            path="settings"
            element={
              <ProtectedRoute requireRole="admin">
                <SettingsPage />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}
